import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { EditorState } from 'draft-js';
import { Macros } from 'interfaces/Macros';
import { Coin, UserAction } from 'interfaces/MeshMaven';
import { useDispatch } from 'react-redux';
import { RephraseOption } from 'screens/common/RephraseOptions/types';
import { useIsMutationCancelled } from 'utils/helpers/reactQueryHelpers';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getApiBodyFromEditorStateAndMedia, convertTextMacrosToEditorState } from 'services/helpers/post';

import { REACT_QUERY_FEEDBACK_DRAWER_KEYS } from '../constants';
import { useFeedbackData, useMeshMavenGroupId, usePrimaryPanelAction } from '../slice/feedbackSlice';
import { CustomerFeedbackAPIResponse, CustomerFeedbackRequestBody } from '../types/CustomerFeedback';
import { FeedbackAnalysisAPIResponse, FeedbackAnalysisRequestBody } from '../types/FeedbackAnalysis';
import { RephraseTextAPIResponse, RephraseTextRequestBody } from '../types/RephraseText';
import { RequestFeedbackDetails, EMPLOYEE } from '../types/RequestedFeedback';
import { Revision } from '../types/Revision';

const addEmployeeToMacros = (employee: EMPLOYEE, macros: Macros): Macros => ({
  ...macros,
  employees: {
    ...macros.employees,
    [String(employee.id)]: {
      uuid: String(employee.id),
      metadata: {
        displayName: employee.displayName,
      },
    },
  },
});

export const useRequestedFeedbackData = (): RequestFeedbackDetails => {
  const queryClient = useQueryClient();
  const requestFeedbackDetails = queryClient
    .getQueryCache()
    .findAll([REACT_QUERY_FEEDBACK_DRAWER_KEYS.fillRequestedFeedback])
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)?.[0]?.state.data;
  return requestFeedbackDetails as RequestFeedbackDetails;
};

const getFeedbackAnalysis = ({
  groupId,
  revisionNumber,
  feedbackType,
  text,
  macros,
}: FeedbackAnalysisRequestBody): Promise<FeedbackAnalysisAPIResponse> =>
  sendRequest({
    url: endpoints.performanceReview.continuousFeedback.analyze,
    method: 'POST',
    credentials: 'include',
    body: {
      groupId,
      revisionNumber,
      feedbackType: feedbackType === 'APPRECIATIVE' ? 'KUDOS' : 'FEEDBACK',
      text,
      macros,
      serviceName: 'cf_analyze',
    },
    queryParams: {},
  });

export const useFeedbackAnalysisMutation = () => {
  const dispatch = useDispatch();
  const meshMavenGroupId = useMeshMavenGroupId();

  const action = usePrimaryPanelAction();
  const { feedbackSentimentType, setAnalysisError, setRevisionByRevisionNumber } = useFeedbackData();

  const requestFeedbackDetails = useRequestedFeedbackData();
  const isMutationCancelled = useIsMutationCancelled();

  return useMutation<
    FeedbackAnalysisAPIResponse,
    { message?: string; status?: number },
    { revision: Revision; revisionNumber: number; editorState: EditorState }
  >(
    ({ revisionNumber, editorState }) => {
      const editorMetadata: { text: string; macros: Macros } = getApiBodyFromEditorStateAndMedia(
        editorState,
        null,
        false,
        feedbackSentimentType
      );

      const macros =
        action === 'REQUESTED' && requestFeedbackDetails?.employee
          ? addEmployeeToMacros(requestFeedbackDetails.employee, editorMetadata.macros)
          : editorMetadata.macros;

      return getFeedbackAnalysis({
        groupId: meshMavenGroupId,
        revisionNumber: revisionNumber + 1,
        feedbackType: feedbackSentimentType,
        text: editorMetadata.text,
        macros,
      });
    },
    {
      mutationKey: [REACT_QUERY_KEYS.FeedbackAnalysis],
      onSuccess: (data, { revision, revisionNumber }) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.FeedbackAnalysis])) {
          return;
        }
        if (data.error) {
          dispatch(setAnalysisError({ error: data.message }));
        } else if (data.entity) {
          dispatch(setAnalysisError({ error: null }));
          dispatch(
            setRevisionByRevisionNumber({
              revisionNumber,
              revision: {
                ...revision,
                feedbackAnalysis: data.entity.labels,
                isReanalyzeEnabled: false,
              },
            })
          );
        } else {
          dispatch(setAnalysisError({ error: 'Uh-oh! Looks like an unknown error happened.' }));
        }
      },
      onError: (error, { revision, revisionNumber }) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.FeedbackAnalysis])) {
          return;
        }
        if (error.status === 500 || !error.status) {
          dispatch(
            setRevisionByRevisionNumber({
              revisionNumber,
              revision: {
                ...revision,
                isReanalyzeEnabled: true,
              },
            })
          );
        }
        dispatch(setAnalysisError({ error: error?.message ?? 'Uh-oh! Looks like an unknown error happened.' }));
      },
    }
  );
};

export const useFeedbackAnalysisLoading = () =>
  useIsMutating({
    mutationKey: [REACT_QUERY_KEYS.FeedbackAnalysis],
  }) > 0;

const rephraseText = ({
  groupId,
  revisionNumber,
  feedbackType,
  text,
  macros,
  rephraseInstruction,
}: RephraseTextRequestBody): Promise<RephraseTextAPIResponse> =>
  sendRequest({
    url: endpoints.performanceReview.continuousFeedback.rephrase,
    method: 'POST',
    credentials: 'include',
    body: {
      groupId,
      revisionNumber,
      feedbackType: feedbackType === 'APPRECIATIVE' ? 'KUDOS' : 'FEEDBACK',
      text,
      macros,
      serviceName: 'cf_rephrase',
      rephraseInstruction,
    },
    queryParams: {},
  });

export const useRephraseTextMutation = () => {
  const dispatch = useDispatch();
  const meshMavenGroupId = useMeshMavenGroupId();

  const action = usePrimaryPanelAction();
  const {
    feedbackSentimentType,
    revisionNumber,
    revisions,
    selectedRevision,
    setAddRevision,
    setRevisionNumber,
    setRephraseError,
    setRevisionByRevisionNumber,
  } = useFeedbackData();

  const { mutate: analyzeFeedback } = useFeedbackAnalysisMutation();

  const requestFeedbackDetails = useRequestedFeedbackData();
  const isMutationCancelled = useIsMutationCancelled();

  return useMutation<
    { data: RephraseTextAPIResponse; macros: Macros },
    { message?: string },
    {
      rephraseInstruction: RephraseOption;
    }
  >(
    ({ rephraseInstruction }) => {
      const editorMetadata: { text: string; macros: Macros } = getApiBodyFromEditorStateAndMedia(
        selectedRevision.updatedState,
        null,
        false,
        feedbackSentimentType
      );

      const macros =
        action === 'REQUESTED' && requestFeedbackDetails?.employee
          ? addEmployeeToMacros(requestFeedbackDetails.employee, editorMetadata.macros)
          : editorMetadata.macros;

      return rephraseText({
        groupId: meshMavenGroupId,
        revisionNumber: revisionNumber + 1,
        feedbackType: feedbackSentimentType,
        text: editorMetadata.text,
        macros,
        rephraseInstruction,
      }).then((data) => ({ data, macros }));
    },
    {
      mutationKey: [REACT_QUERY_KEYS.RephraseText],
      onSuccess: ({ data, macros }) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.RephraseText])) {
          return;
        }
        if (data.error) {
          dispatch(setRephraseError({ error: data.message }));
        } else if (data.entity) {
          const newEditorState = convertTextMacrosToEditorState(data.entity.rephrasedText, macros);
          const newRevision = {
            requestId: data.entity.requestId,
            originalState: newEditorState,
            updatedState: newEditorState,
            isAIFeedbackProvided: false,
            isReanalyzeEnabled: false,
            feedbackAnalysis: { context: 0, observations: 0, impact: 0, next_steps: 0 } as Coin,
          };

          dispatch(
            setRevisionByRevisionNumber({
              revisionNumber,
              revision: {
                ...selectedRevision,
                isReanalyzeEnabled: false,
              },
            })
          );
          dispatch(
            setAddRevision({
              revision: newRevision,
            })
          );
          dispatch(setRevisionNumber({ revisionNumber: revisions.length }));
          analyzeFeedback({ revision: newRevision, revisionNumber: revisions.length, editorState: newEditorState });
          dispatch(setRephraseError({ error: null }));
        } else {
          dispatch(setRephraseError({ error: 'Uh-oh! Looks like an unknown error happened.' }));
        }
      },
      onError: (error) => {
        if (isMutationCancelled([REACT_QUERY_KEYS.RephraseText])) {
          return;
        }
        dispatch(setRephraseError({ error: error?.message ?? 'Uh-oh! Looks like an unknown error happened.' }));
      },
    }
  );
};

export const useRephraseTextLoading = () =>
  useIsMutating({
    mutationKey: [REACT_QUERY_KEYS.RephraseText],
  }) > 0;

const customerFeedback = ({
  userAction,
  requestId,
  recommendedContent,
  usedContent,
}: CustomerFeedbackRequestBody): Promise<CustomerFeedbackAPIResponse> =>
  sendRequest({
    url: endpoints.performanceReview.continuousFeedback.feedback,
    method: 'POST',
    credentials: 'include',
    body: {
      userAction,
      requestId,
      recommendedContent,
      usedContent,
      serviceName: 'cf_rephrase',
    },
    queryParams: {},
  });

export const useCustomerFeedbackMutation = () => {
  const dispatch = useDispatch();
  const { revisionNumber, setRevisionByRevisionNumber, selectedRevision } = useFeedbackData();

  return useMutation<any, { message?: string }, { userAction: UserAction }>(
    ({ userAction }) =>
      customerFeedback({
        userAction,
        requestId: selectedRevision.requestId,
        recommendedContent: {
          comment: selectedRevision.originalState.getCurrentContent().getPlainText('\n'),
        },
        usedContent: {
          comment: selectedRevision.updatedState.getCurrentContent().getPlainText('\n'),
        },
      }),
    {
      mutationKey: [REACT_QUERY_KEYS.CustomerFeedback],
      onSuccess: (_, { userAction }) => {
        dispatch(
          setRevisionByRevisionNumber({
            revisionNumber,
            revision: {
              ...selectedRevision,
              isAIFeedbackProvided: userAction !== 'USE',
            },
          })
        );
      },
    }
  );
};

export const useCustomerFeedbackLoading = () =>
  useIsMutating({
    mutationKey: [REACT_QUERY_KEYS.CustomerFeedback],
  }) > 0;
